import { useState, type FormEvent } from 'react';
import { CalendarPlusIcon, PlusIcon, XIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { undoToast } from '../ui/undoToast';
import { pillars, pillarByKey } from './socialOptions';
import { inputCls } from '../ui/styles';
import { uniqueSlug } from '../editing';
import type { PillarKey, SocialIdea } from '../../core/types/social';
type Props = {
  ideas: SocialIdea[];
  onChange: (ideas: SocialIdea[]) => void;
  /** Maakt van een idee een post en opent die. */
  onPlan: (idea: SocialIdea) => void;
};
const isPillar = (value: string): value is PillarKey => pillars.some(p => p.key === value);

/** Ideeën vangen op het moment dat ze komen — tussen twee cliënten door. */
export function IdeaBank({
  ideas,
  onChange,
  onPlan
}: Props) {
  const [text, setText] = useState('');
  const [pillar, setPillar] = useState<PillarKey>('kennis');
  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    const id = uniqueSlug(`idee-${Date.now().toString(36)}`, ideas.map(i => i.id));
    onChange([{
      id,
      text: text.trim(),
      pillar
    }, ...ideas]);
    setText('');
  };
  return <Panel title="Ideeënbank" description="Een vraag van een cliënt, iets wat je opviel? Zet het hier, plan het later." data-id="element-4542" data-mp="7pnxptm3lnqu">
      <form onSubmit={add} className="flex flex-col gap-2 sm:flex-row" data-id="element-4543" data-mp="3p302dlpddto0">
        <label className="sr-only" htmlFor="idea-text" data-id="element-4544" data-mp="3u4fmnbjq3w5c">
          Nieuw idee
        </label>
        <input id="idea-text" value={text} onChange={e => setText(e.target.value)} placeholder="Bijv. waarom je huid trekt na het sporten" className={`${inputCls} sm:flex-1`} data-id="element-4545" data-mp="lonmr2diw4nj" />
        <div className="flex gap-2" data-id="element-4546" data-mp="bsawyfl3may2">
          <label className="sr-only" htmlFor="idea-pillar" data-id="element-4547" data-mp="go87u6dlep3g">
            Onderwerp
          </label>
          <select id="idea-pillar" value={pillar} onChange={e => isPillar(e.target.value) && setPillar(e.target.value)} className={`${inputCls} flex-1 sm:w-44`} data-id="element-4548" data-mp="2wfw2tw91q7jm">
            {pillars.map(p => <option key={p.key} value={p.key} data-id="element-4549" data-mp="12xgfs3hkg18i">
                {p.label}
              </option>)}
          </select>
          <button type="submit" disabled={!text.trim()} aria-label="Idee toevoegen" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-ink text-shell transition-colors duration-150 ease-soft hover:bg-clay disabled:bg-ink/30 sm:h-10 sm:w-10" data-id="element-4550" data-mp="y5yetffjm0nj">
            <PlusIcon className="h-4 w-4" strokeWidth={2} data-id="element-4551" data-mp="k5dxknpa0cw5" />
          </button>
        </div>
      </form>

      {ideas.length === 0 ? <p className="mt-5 text-[13px] text-muted" data-id="element-4552" data-mp="a8rcpywy9lu4">Alle ideeën zijn ingepland. Goed bezig.</p> : <ul className="mt-4 divide-y divide-line" data-id="element-4553" data-mp="29ln267wpl1ov">
          {ideas.map(idea => {
        const p = pillarByKey(idea.pillar);
        return <li key={idea.id} className="flex items-start gap-3 py-3" data-id="element-4554" data-mp="24nbvg61pn7av">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${p.dot}`} title={p.label} aria-hidden="true" data-id="element-4555" data-mp="2c59zvw51bs78" />
                <span className="min-w-0 flex-1" data-id="element-4556" data-mp="2h3tne0za1c1p">
                  <span className="block text-[14px] leading-snug text-ink" data-id="element-4557" data-mp="2lwef8wymhf2l">{idea.text}</span>
                  <span className="block text-[12px] text-muted" data-id="element-4558" data-mp="2quy0sw9b0ouf">{p.label}</span>
                </span>
                <button type="button" onClick={() => onPlan(idea)} aria-label={`Inplannen: ${idea.text}`} className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-2 text-[13px] text-clay transition-colors duration-150 ease-soft hover:bg-sand hover:text-ink" data-id="element-4559" data-mp="1t7p9gkmmglki">
                  <CalendarPlusIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-4560" data-mp="282n11b9y570j" />
                  <span className="hidden sm:inline" aria-hidden="true" data-id="element-4561" data-mp="3eka6vn9c4gsi">
                    Inplannen
                  </span>
                </button>
                <button type="button" onClick={() => {
            // Direct weg, maar met "Terugzetten": een idee is snel per ongeluk weggetikt.
            const before = ideas;
            onChange(ideas.filter(i => i.id !== idea.id));
            undoToast('Idee verwijderd', () => onChange(before), {
              restored: 'Idee teruggezet'
            });
          }} aria-label={`Idee verwijderen: ${idea.text}`} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted transition-colors duration-150 ease-soft hover:bg-sand hover:text-ink" data-id="element-4562" data-mp="3h4yevdqurprj">
                  <XIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-4563" data-mp="8pep4ope4ph8" />
                </button>
              </li>;
      })}
        </ul>}
    </Panel>;
}